import { app, component, Component, ojs } from "modular-openscriptjs";
import { dom } from "../../fotastart";
import {
  either,
  extractNonObjectPairs,
  hasPlaceholderShown,
  uniqueId,
} from "../../helpers";

const h = app("h");

export default class FormField extends Component {
  render(config, ...args) {
    // Direct attrs for `config` (non-object pairs) are for the <input>
    // The rest of the attrs are nested in `label`, `icons`, `helpText`
    const hook = `ff-${config?.uid ?? uniqueId()}`;
    const tag = config?.tag ?? "input";
    const inputAttrs = extractNonObjectPairs(config);
    const label = config?.label?.value ?? "Label";
    const hasFloatingLabel = config?.label?.floats ?? true;
    const leadingIcon = config?.icons?.leading;
    const trailingIcon = config?.icons?.trailing;
    const hasLeadingIcon = leadingIcon ? true : false;
    const hasTrailingIcon = trailingIcon ? true : false;
    const helpText = config?.helpText?.value;
    const dependencies = config?.dependencies ?? [];
    delete inputAttrs.tag;
	const additionalListeners = config?.listeners ?? {};
	
	if(additionalListeners && Object.keys(additionalListeners).length > 0) {
		delete config.listeners;
	}

    // The event to fire to the dependent input elements
    // when the input event is fired on this element.
    // To avoid clashing with element's input event,
    // we use a CustomEvent by default.
    const dependencyEventOnInput =
      config?.dependencyEventOnInput ?? "dependencyInput";

    // Values that shouldn't be empty:
    // `id` is needed for the label,
    // `type` is needed to set a default,
    // `placeholder` is needed for floating labels (CSS)
    const id = config?.id ?? hook;
    const type = config?.type ?? "text";
    const placeholder = config?.placeholder ?? "...";

    // Check for adding .has-placeholder-shown class if value is empty
    const value = config?.value ?? "";
    const showPlaceholder = value.length == 0;

    // The input element's value will be its innerHTML for
    // editable <div>. Use a variable to achieve that.
    if (tag == "TextArea") delete inputAttrs.value;

    // setup listeners
    const listeners = {
      focus: [
        function () {
          dom.get(`[data-ff-wrapper=${hook}]`).classList.add("has-focus");
        },
      ],
      blur: [
        function () {
          dom.get(`[data-ff-wrapper=${hook}]`).classList.remove("has-focus");
        },
      ],
      input: [
        (event) => {
          if (event instanceof CustomEvent) {
            // The signature of the firer of this event.
            // do what you want to do with this information.
            const signature = event.detail.signature;
            console.log(`Artificial 'input' Event`, signature);
          } else {
            component(this.id).placeLabelAccordingly(event.target, hook);

            // This event is organic as per our standards
            // we can notify all dependencies of an
            // input change, i.e. fire a CustomEvent
            // on all dependent input fields!

            // We use `ff-dep-hook` as the dependency
            // attribute that's passed to this element
            // through the `inputAttrs`.

            let currentDepHook = event.target.getAttribute("ff-dep-hook");

            for (let depHook of dependencies) {
              // Ensure it's not this element
              if (depHook == currentDepHook) continue;

              let depInput = dom.get(`[ff-dep-hook="${depHook}"]`);

              if (!depInput) continue;

              // Dispatch custom input event
              let e = new CustomEvent(dependencyEventOnInput, {
                bubbles: true,
                cancelable: true,
                detail: {
                  // This event came from this input
                  // You can use this for anything
                  signature: currentDepHook,
                },
              });

              depInput.dispatchEvent(e);
            }
          }
        },
      ],
      [dependencyEventOnInput]: [
        (event) => {
          component(this.id).placeLabelAccordingly(event.target, hook);
        },
      ],
    };

	// lets merge the additional listeners
	if(additionalListeners && Object.keys(additionalListeners).length > 0) {
		for(let eventName in additionalListeners) {
			listeners[eventName] ??= [];

			let handlers = additionalListeners[eventName];
			
			if(Array.isArray(handlers)) {
				listeners[eventName].push(...handlers);
			} else {
				listeners[eventName].push(handlers);
			}
		}
	}

    const inputField = h[tag]({
      class: "form-control form-field-input",
      type: type,
      id: id,
      placeholder: placeholder,
      data_ff_input: hook,
      listeners,
      ...inputAttrs,
    });

    if (tag == "TextArea") inputField.innerHTML = value;

    return h.div(
      {
        class: [
          "form-field-wrapper",
          hasFloatingLabel ? "has-floating-label" : "",
          hasLeadingIcon ? "has-leading-icon" : "",
          hasTrailingIcon ? "has-trailing-icon" : "",
          showPlaceholder ? "has-placeholder-shown" : "",
        ]
          .join(" ")
          .trim(),
        data_ff_wrapper: hook,
      },
      h.div(
        { class: "form-field" },
        either(hasLeadingIcon, () =>
          h.span(
            {
              class: "form-field-icon form-field-icon-leading",
              data_ff_icon_leading: hook,
            },
            leadingIcon,
          ),
        ),
        either(hasTrailingIcon, () =>
          h.span(
            {
              class: "form-field-icon form-field-icon-trailing",
              data_ff_icon_trailing: hook,
            },
            trailingIcon,
          ),
        ),
        inputField,
      ),
      h.label(
        {
          class: [
            "form-field-label",
            hasFloatingLabel ? "form-field-label-floating" : "",
          ]
            .join(" ")
            .trim(),
          data_ff_label: hook,
          for: id,
        },
        h.span(label),
      ),
      either(helpText, () =>
        h.div({ class: "form-field-text", data_ff_text: hook }, helpText),
      ),
      ...args,
    );
  }

  placeLabelAccordingly(input, hook) {
    const wrap = dom.get(`[data-ff-wrapper=${hook}]`);
    wrap.classList.toggle("has-placeholder-shown", hasPlaceholderShown(input));
    if (typeof input.checkValidity === "function") {
      wrap.classList.toggle("is-invalid", !input.checkValidity());
    }
  }
}

ojs(FormField);
